import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCapabilities } from "@/features/capabilities/hooks/useCapabilities";
import type { CapabilityModuleKey } from "@/features/capabilities/types";
import { useAuthStore } from "@/lib/auth/authStore";
import { EliteEmptyState } from "@/shared/ui";
import type { BusinessRole, ProfileRole, SystemRole } from "@/types/roles";
import { CLINICAL_ROUTE_FRAGMENTS } from "@/types/roles";

export interface RoleGuardProps {
  children: ReactNode;
  systemRoles?: readonly SystemRole[];
  profileRoles?: readonly ProfileRole[];
  businessRoles?: readonly BusinessRole[];
  moduleKey?: CapabilityModuleKey;
  redirectTo?: string;
  redirectMessage?: string;
  requiredPermissions?: readonly string[];
}

const capabilityRequirementsByModule: Partial<Record<CapabilityModuleKey, readonly string[]>> = {
  chat: ["messages.general"],
};

const GuardState = ({
  description,
}: {
  title: string;
  description: string;
  danger?: boolean;
}): JSX.Element => (
  <EliteEmptyState
    title="Mbrojtja e Privatësisë Mjekësore"
    description={description}
    icon={<ShieldAlert className="h-7 w-7" aria-hidden="true" />}
  />
);

export const RoleGuard = ({
  children,
  systemRoles,
  profileRoles,
  businessRoles,
  moduleKey,
  redirectTo,
  redirectMessage,
  requiredPermissions,
}: RoleGuardProps): JSX.Element => {
  const { systemRole, profileRole, businessRole, user, capabilities } = useAuthStore();
  const capabilitiesQuery = useCapabilities();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isClinicalPath = CLINICAL_ROUTE_FRAGMENTS.some((fragment) => pathname.includes(fragment));
  const isSupportModeClinicalPath = isClinicalPath && Boolean(user?.isImpersonating);
  const isAdminTryingClinical = isClinicalPath && (systemRole === "admin" || systemRole === "superadmin");
  const isTechnicianProfessionalProfilePath =
    pathname.startsWith("/app/professional/profile") ||
    pathname.startsWith("/app/professional/settings") ||
    pathname.startsWith("/app/professional/career") ||
    pathname.startsWith("/app/professional/sections");
  const isTechnicianInClinicianWorkbench =
    profileRole === "technician" &&
    pathname.startsWith("/app/professional") &&
    !isTechnicianProfessionalProfilePath;
  const isStudentInClinicianWorkbench = profileRole === "student" && pathname.startsWith("/app/professional") && !pathname.startsWith("/app/professional/profile");
  const isStudentOutsideSandbox =
    profileRole === "student" && pathname.startsWith("/app") && !pathname.startsWith("/app/professional/profile");
  const moduleCapability = moduleKey ? capabilitiesQuery.data?.modules[moduleKey] : undefined;
  const activeCapabilities = capabilities.length > 0 ? capabilities : user?.permissions ?? [];
  const moduleRequiredCapabilities = moduleKey ? capabilityRequirementsByModule[moduleKey] : undefined;
  const moduleCapabilitiesAllowed =
    !moduleRequiredCapabilities ||
    activeCapabilities.length === 0 ||
    moduleRequiredCapabilities.every((capability) => activeCapabilities.includes(capability));
  const permissionsAllowed =
    !requiredPermissions ||
    requiredPermissions.every((permission) => activeCapabilities.includes(permission));
  const profileAllowed = !profileRoles || (profileRole !== null && profileRoles.includes(profileRole));
  const businessAllowed = !businessRoles || (businessRole !== null && businessRoles.includes(businessRole));
  // Kur nje route pranon si profil profesional ashtu edhe rol biznesi,
  // mjafton nje context i autorizuar. P.sh. laboratori per technician OSE owner.
  const contextAllowed = profileRoles && businessRoles
    ? profileAllowed || businessAllowed
    : profileAllowed && businessAllowed;
  const allowed =
    (!systemRoles || systemRoles.includes(systemRole)) &&
    contextAllowed;

  useEffect(() => {
    if (!isSupportModeClinicalPath && !isAdminTryingClinical && !isTechnicianInClinicianWorkbench && !isStudentInClinicianWorkbench && allowed) return;
    console.warn("[AUTH AUDIT]", {
      event: isSupportModeClinicalPath ? "support_mode_clinical_access_blocked" : isAdminTryingClinical ? "clinical_access_blocked" : isTechnicianInClinicianWorkbench || isStudentInClinicianWorkbench ? "clinical_workbench_role_blocked" : "access_denied",
      path: pathname,
      systemRole,
      profileRole,
      businessRole,
      required: { systemRoles, profileRoles, businessRoles },
      ts: new Date().toISOString(),
    });
  }, [allowed, isSupportModeClinicalPath, isAdminTryingClinical, isTechnicianInClinicianWorkbench, isStudentInClinicianWorkbench, pathname, systemRole, profileRole, businessRole, systemRoles, profileRoles, businessRoles]);

  if (isSupportModeClinicalPath) {
    return (
      <GuardState
        danger
        title="Mbrojtja e Privatësisë Mjekësore"
        description="Qasja klinike është e bllokuar gjatë sesionit të mbështetjes për të mbrojtur privatësinë e pacientit."
      />
    );
  }
  if (isAdminTryingClinical) {
    return (
      <GuardState
        danger
        title={t("access.clinicalForbidden.title")}
        description={t("access.clinicalForbidden.description")}
      />
    );
  }
  if (isTechnicianInClinicianWorkbench || isStudentInClinicianWorkbench) {
    return (
      <GuardState
        danger
        title={t("access.denied.title")}
        description={t("access.technicianBlock")}
      />
    );
  }
  if (isStudentOutsideSandbox) return <Navigate to="/app/professional/profile" replace />;

  if (moduleKey && moduleCapability && !moduleCapability.enabled) {
    return (
      <GuardState
        title={t("access.moduleInactive.title")}
        description={moduleCapability.reason ?? t("access.moduleInactive.description")}
      />
    );
  }

  if (!moduleCapabilitiesAllowed) {
    return (
      <GuardState
        danger
        title={t("access.permissionMissing.title")}
        description={t("access.permissionMissing.description", { permissions: moduleRequiredCapabilities?.join(", ") })}
      />
    );
  }

  if (profileRoles && profileRole === null && (!businessRoles || !businessAllowed)) {
    if (redirectTo && (systemRole === "admin" || systemRole === "superadmin")) {
      return <Navigate to={redirectTo} replace state={{ accessError: redirectMessage ?? t("access.clinicalForbidden.description") }} />;
    }
    return <GuardState title={t("access.profileRequired.title")} description={t("access.profileRequired.description")} />;
  }

  if (!permissionsAllowed) {
    return (
      <GuardState
        danger
        title={t("access.permissionMissing.title")}
        description={t("access.permissionMissing.description", { permissions: requiredPermissions?.join(", ") })}
      />
    );
  }

  if (!allowed) {
    if (redirectTo) {
      return (
        <Navigate
          to={redirectTo}
          replace
          state={{ accessError: redirectMessage ?? t("access.denied.description", { role: t(`roles.${systemRole}`) }) }}
        />
      );
    }
    return (
      <GuardState
        danger
        title={t("access.denied.title")}
        description={t("access.denied.description", { role: t(`roles.${systemRole}`) })}
      />
    );
  }

  return <>{children}</>;
};
